import { ReactElement } from 'react';
import styles from './HealthTooltip.module.scss';
import { CustomTooltipProps } from 'ag-grid-react';
import { ISelfTestFailure } from '@customer/protobuf/raw-tracks';
import { useTranslation } from 'react-i18next';

interface StrictSelfTestFailure extends ISelfTestFailure {
  Category: number;
  Level: number;
  Id: number;
}

const isValidSelfTestFailure = (failure: ISelfTestFailure): failure is StrictSelfTestFailure => {
  return (
    typeof failure.Category === 'number' &&
    typeof failure.Level === 'number' &&
    typeof failure.Id === 'number'
  );
};

// Don't return a tooltip if the selfTestFailures array is empty or undefined.
export const HealthTooltip = <T,>({
  value: selfTestFailures,
}: CustomTooltipProps<T, ISelfTestFailure[]>): ReactElement | null => {
  const { t } = useTranslation();

  // Exit if the value is missing or empty
  if (!selfTestFailures?.length) return null;

  const validSelfTestFailures: StrictSelfTestFailure[] =
    selfTestFailures.filter(isValidSelfTestFailure);

  // Exit if none of the array items had a suitable data shape.
  if (!validSelfTestFailures.length) return null;

  let highestLevel = 0;
  let failuresToDisplay: StrictSelfTestFailure[] = [];

  // Search from the highest level down for a list of failures, and stop once a list if found.
  for (let level = 1; level <= 3; level++) {
    const failuresInLevel = validSelfTestFailures.filter(failure => failure.Level === level);

    if (failuresInLevel.length) {
      highestLevel = level;
      failuresToDisplay = failuresInLevel;
      // Stop the search.
      break;
    }
  }

  if (highestLevel > 0) {
    return (
      <div className={styles.tooltip}>
        <div className={styles.level}>{t('ActiveLevelFaults', { level: highestLevel })}</div>
        <div className={styles.faults}>
          {failuresToDisplay.map(({ Id }: StrictSelfTestFailure) => {
            return (
              <div key={Id} className={styles.faults__item}>
                {t(`FaultTest_${Id}`)}
              </div>
            );
          })}
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default HealthTooltip;
